import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { combineLatest } from 'rxjs';

interface ErrorConfig {
  title: string;
  message: string;
  buttonText: string;
  redirectUrl: string;
  image: string;
}

@Component({
  selector: 'app-error-reusable',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './error-reusable.html',
  styleUrls: ['./error-reusable.css'],
})
export class ErrorReusableComponent implements OnInit {
  errorCode: string = '404';

  errorConfig: Record<string, ErrorConfig> = {
    '401': {
      title: 'Unauthorized',
      message: 'You need to login to access this page.',
      buttonText: 'Go to Login',
      redirectUrl: '/login',
      image: '/Error-images/401 (2).svg',
    },
    '402': {
      title: 'Access Restricted',
      message: 'You don\u2019t have permission to view this page.',
        buttonText: 'Back to Login',
        redirectUrl: '/login',
      image: '/Error-images/402.svg',
    },
    '404': {
      title: 'Page Not Found',
      message: 'The page you are looking for does not exist.',
      buttonText: 'Go Home',
      // Straight to the dashboard. `/` redirects to /login, whose guard sends a signed-in
      // user back to the last page they were on — not home. Every app's dashboard is
      // guarded, so a signed-out user still ends up at its login.
      redirectUrl: '/dashboard',
      image: '/Error-images/404.svg',
    },
  };

  errorData: ErrorConfig = this.errorConfig['404'];

  private readonly destroyRef = inject(DestroyRef);
  /** Superadmin is zoneless: a value set from a route emission has to ask for a render. */
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // The router keeps this component when only the URL's params change (/error/404 →
    // /error/402, or one unknown URL to another), so it follows the route instead of
    // reading it once.
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, query]) => this.applyError(params.get('code'), query.get('message')));
  }

  private applyError(code: string | null, overrideMessage: string | null): void {
    this.errorCode = code ?? '404';

    const config = this.errorConfig[this.errorCode] || this.errorConfig['404'];

    this.errorData = {
      ...config,
      message: overrideMessage?.trim() || config.message,
      image: config.image || '/Error-images/404.svg',
    };
    this.cdr.markForCheck();
  }

  onActionClick(): void {
    if (this.errorCode === '401') {
      void this.router.navigateByUrl('/login', { replaceUrl: true });
      return;
    }

    // A 404 means a bad URL, not a bad session — sending the user home must not
    // sign them out. The bad URL is replaced, so Back doesn't return to it.
    void this.router.navigateByUrl(this.errorData.redirectUrl, { replaceUrl: true });
  }
}
